<template>
  <Layout>
    <div class="tpl-builder">

      <!-- ══════ STEP 1: Informações básicas (fullscreen centered) ══════ -->
      <template v-if="step === 1">
        <div class="tpl-builder-header">
          <div class="header-left">
            <button class="btn-back" @click="$router.push('/wa/template')"><i class="bx bx-arrow-back"></i></button>
            <span class="header-title">Criar novo template</span>
          </div>
          <div class="header-right">
            <button class="btn-action-primary" @click="goToStep2">
              Continuar <i class="bx bx-right-arrow-alt"></i>
            </button>
          </div>
        </div>

        <div class="step1-content">
          <div class="step1-inner">
            <!-- Categoria -->
            <div class="field-group">
              <div class="field-title">Categoria</div>
              <div class="field-desc">Escolha a categoria que melhor descreve seu template (regra Meta).</div>
              <div class="cat-grid">
                <div class="cat-card" :class="{ selected: form.category === 'MARKETING' }" @click="form.category = 'MARKETING'">
                  <div class="cat-icon" :class="{ active: form.category === 'MARKETING' }">
                    <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M3 3h18v18H3zM3 9h18M9 21V9"/></svg>
                  </div>
                  <div>
                    <div class="cat-name">Marketing</div>
                    <div class="cat-desc">Promoções, ofertas, anúncios e novidades sobre eventos.</div>
                  </div>
                </div>
                <div class="cat-card" :class="{ selected: form.category === 'UTILITY' }" @click="form.category = 'UTILITY'">
                  <div class="cat-icon" :class="{ active: form.category === 'UTILITY' }">
                    <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  </div>
                  <div>
                    <div class="cat-name">Utilidade</div>
                    <div class="cat-desc">Confirmações, lembretes e atualizações de inscrição/pedido.</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Nome -->
            <div class="field-group">
              <div class="field-title">Nome do template</div>
              <div class="field-desc">Use letras minúsculas, números e underscores. Ex: confirmacao_inscricao</div>
              <div class="input-wrap">
                <input v-model="form.name" class="tpl-input" placeholder="nome_do_template" :class="{ error: (submitted && !form.name) || nameError }" @input="onNameInput">
                <span class="counter">{{ (form.name || '').length }}/512</span>
              </div>
              <div v-if="submitted && !form.name" class="field-error">Nome é obrigatório.</div>
              <div v-else-if="nameError" class="field-error">{{ nameError }}</div>
              <div v-else-if="form.name && !nameError" class="field-hint">
                <i class="bx bx-check-circle" style="color:#22C55E"></i> Nome válido
              </div>
            </div>

            <!-- Idioma -->
            <div class="field-group">
              <div class="field-title">Idioma</div>
              <div class="field-desc">Os templates Eventfy são criados em Português (Brasil).</div>
              <div class="lang-box">
                <svg width="22" height="15" viewBox="0 0 22 15"><rect width="22" height="15" rx="2" fill="#009739"/><polygon points="11,1.5 20.5,7.5 11,13.5 1.5,7.5" fill="#FEDD00"/><circle cx="11" cy="7.5" r="3.8" fill="#012169"/></svg>
                <span>Português (Brasil)</span>
                <span class="lang-lock"><svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg> fixo</span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- ══════ STEP 2: Componentes + Preview (fullscreen split) ══════ -->
      <template v-if="step === 2">
        <div class="tpl-builder-header">
          <div class="header-left">
            <button class="btn-back" @click="step = 1"><i class="bx bx-arrow-back"></i></button>
            <span class="header-title">{{ isEditing ? 'Editar template' : 'Criar novo template' }}</span>
          </div>
          <div class="header-right">
            <button class="btn-action-secondary" @click="$router.push('/wa/template')">Cancelar</button>
            <button class="btn-action-primary" @click="handleSubmit" :disabled="saving">
              <i v-if="saving" class="bx bx-loader-alt bx-spin"></i>
              {{ saving ? 'Salvando...' : (isEditing ? 'Salvar' : 'Criar Template') }}
            </button>
          </div>
        </div>

        <div class="tpl-builder-content">
          <!-- Main (scrollable) -->
          <div class="tpl-panel-main">
            <div class="tpl-panel-scroll">

              <!-- Header -->
              <div class="section">
                <div class="section-head">
                  <div class="field-title">Cabeçalho <span class="tag-opt">Opcional</span></div>
                  <div class="field-desc">Adicione um título ou mídia ao topo da mensagem.</div>
                </div>
                <div class="pill-group">
                  <button class="pill" :class="{ active: form.headerType === 'NONE' }" @click="setHeaderType('NONE')">Nenhum</button>
                  <button class="pill" :class="{ active: form.headerType === 'TEXT' }" @click="setHeaderType('TEXT')">Texto</button>
                  <button class="pill" :class="{ active: isMediaHeader }" @click="setHeaderType('IMAGE')">Mídia</button>
                  <button class="pill" :class="{ active: form.headerType === 'QRCODE_PARTICIPANT' }" @click="setHeaderType('QRCODE_PARTICIPANT')">QRCode do Participante</button>
                </div>

                <!-- QRCode dinâmico do participante: imagem-sample fica no servidor,
                     enviada automaticamente pra Meta na hora da aprovação. No envio
                     real, cada participante recebe o próprio QR único. -->
                <div v-if="form.headerType === 'QRCODE_PARTICIPANT'" class="qr-info-box mt-3">
                  <div class="qr-info-icon">
                    <svg width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                      <rect x="3" y="3" width="7" height="7" rx="1"/>
                      <rect x="14" y="3" width="7" height="7" rx="1"/>
                      <rect x="3" y="14" width="7" height="7" rx="1"/>
                      <path d="M14 14h3v3h-3zM18 18h3v3h-3z"/>
                    </svg>
                  </div>
                  <div class="qr-info-content">
                    <div class="qr-info-title">QRCode dinâmico do participante</div>
                    <div class="qr-info-desc">
                      Cada participante receberá o próprio QR Code único no header.
                      Para a aprovação na Meta, será usada uma imagem de exemplo
                      gerada automaticamente — você não precisa fazer upload.
                    </div>
                  </div>
                </div>
                <div v-if="form.headerType === 'TEXT'" class="mt-3">
                  <div class="input-wrap">
                    <input v-model="form.headerText" class="tpl-input" placeholder="Título do cabeçalho" maxlength="60">
                    <span class="counter">{{ (form.headerText || '').length }}/60</span>
                  </div>
                </div>
                <div v-if="isMediaHeader" class="mt-3">
                  <div class="media-grid">
                    <div class="media-opt" :class="{ selected: form.headerType === 'IMAGE' }" @click="form.headerType = 'IMAGE'">
                      <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-5-5L5 21"/></svg>
                      <span>Imagem</span>
                    </div>
                    <div class="media-opt" :class="{ selected: form.headerType === 'VIDEO' }" @click="form.headerType = 'VIDEO'">
                      <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                      <span>Vídeo</span>
                    </div>
                    <div class="media-opt" :class="{ selected: form.headerType === 'DOCUMENT' }" @click="form.headerType = 'DOCUMENT'">
                      <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      <span>Documento</span>
                    </div>
                    <div class="media-opt" :class="{ selected: form.headerType === 'LOCATION' }" @click="form.headerType = 'LOCATION'">
                      <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      <span>Local</span>
                    </div>
                  </div>
                  <div v-if="form.headerType === 'IMAGE'" class="upload-area mt-3" @click="$refs.headerFile.click()">
                    <svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    <span>{{ headerFileName || 'Selecionar imagem de exemplo' }}</span>
                    <small>JPEG ou PNG, máx 5MB</small>
                  </div>
                  <input ref="headerFile" type="file" accept="image/jpeg,image/png" style="display:none" @change="onHeaderFileChange">
                  <div v-if="form.headerType === 'VIDEO'" class="upload-area mt-3" @click="$refs.headerVideoFile.click()">
                    <svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                    <span>{{ headerVideoFileName || 'Selecionar vídeo de exemplo' }}</span>
                    <small>MP4, máx 16MB</small>
                  </div>
                  <input ref="headerVideoFile" type="file" accept="video/mp4" style="display:none" @change="onHeaderVideoChange">
                  <div v-if="form.headerType === 'DOCUMENT'" class="upload-area mt-3" @click="$refs.headerDocumentFile.click()">
                    <svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    <span>{{ headerDocumentFileName || 'Selecionar documento de exemplo' }}</span>
                    <small>PDF, máx 100MB</small>
                  </div>
                  <input ref="headerDocumentFile" type="file" accept="application/pdf" style="display:none" @change="onHeaderDocumentChange">
                  <div v-if="form.headerType === 'LOCATION'" class="location-info mt-3">
                    <div class="location-info-icon"><svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg></div>
                    <div><div class="location-info-title">Header de Localização</div><div class="location-info-desc">A localização é enviada no momento do disparo, não na criação do template.</div></div>
                  </div>
                </div>
              </div>

              <!-- Body -->
              <div class="section">
                <div class="section-head">
                  <div class="field-title">Corpo da mensagem</div>
                  <div class="field-desc">Texto principal. Use variáveis para personalizar. Máx 1024 caracteres.</div>
                </div>
                <div class="input-wrap">
                  <textarea v-model="form.bodyText" class="tpl-input tpl-textarea" rows="6" placeholder="Digite o texto da mensagem..." maxlength="1024" :class="{ error: submitted && !form.bodyText }"></textarea>
                  <span class="counter counter-textarea">{{ (form.bodyText || '').length }}/1024</span>
                </div>
                <div class="toolbar">
                  <div class="toolbar-left">
                    <div class="var-wrap">
                      <button class="toolbar-btn" @click="showVarDropdown = !showVarDropdown">
                        <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg> Variável
                      </button>
                      <div class="var-dropdown" :class="{ show: showVarDropdown }">
                        <button v-for="v in variableOptions" :key="v.key" class="var-dropdown-item" @click="insertNamedVariable(v)">
                          <span class="var-tag">{{ v.tag }}</span>
                          {{ v.label }}
                        </button>
                      </div>
                    </div>
                    <button class="toolbar-btn" @click="wrapSelection('*')">𝐁</button>
                    <button class="toolbar-btn" @click="wrapSelection('_')">𝘐</button>
                    <button class="toolbar-btn" @click="wrapSelection('~')">S̶</button>
                  </div>
                </div>
                <div v-if="submitted && !form.bodyText" class="field-error">Corpo é obrigatório.</div>

                <!-- Variáveis -->
                <div v-if="variableCount > 0" class="var-examples">
                  <div class="field-title" style="font-size:12px">Exemplos das variáveis <small class="text-muted fw-normal">(obrigatório pra aprovação da Meta)</small></div>
                  <div v-for="n in variableCount" :key="n" class="var-example-row">
                    <span class="var-badge" v-text="varLabel(n)"></span>
                    <span class="var-friendly">{{ varFriendlyName(n) }}</span>
                    <input v-model="form.examples[n]" class="tpl-input tpl-input-sm" :placeholder="varPlaceholder(n)">
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="section">
                <div class="section-head">
                  <div class="field-title">Rodapé <span class="tag-opt">Opcional</span></div>
                  <div class="field-desc">Pequena linha de texto na base da mensagem.</div>
                </div>
                <div class="input-wrap">
                  <input v-model="form.footerText" class="tpl-input" placeholder="Ex: Eventfy · Plataforma de eventos" maxlength="60">
                  <span class="counter">{{ (form.footerText || '').length }}/60</span>
                </div>
              </div>

              <!-- Botões -->
              <div class="section">
                <div class="section-head">
                  <div class="field-title">Botões <span class="tag-opt">Opcional</span></div>
                  <div class="field-desc">Adicione até 3 botões de resposta rápida ou link.</div>
                </div>

                <div v-for="(btn, i) in form.buttons" :key="i" class="btn-item">
                  <div class="btn-item-header">
                    <span>{{ btn.type === 'QUICK_REPLY' ? 'Resposta rápida' : (btn.dynamic_followup ? 'Ação de retorno' : 'Visitar website') }}</span>
                    <button class="btn-item-delete" @click="removeButton(i)"><i class="bx bx-trash"></i> Remover</button>
                  </div>
                  <div class="btn-item-body">
                    <div class="field-label">Texto do botão <small class="text-muted">(máx. 25 caracteres)</small></div>
                    <div class="input-wrap">
                      <input v-model="btn.text" class="tpl-input" maxlength="25" placeholder="Ex: Confirmar presença">
                      <span class="counter">{{ (btn.text || '').length }}/25</span>
                    </div>
                  </div>
                  <div v-if="btn.type === 'URL'" class="btn-item-body">
                    <template v-if="btn.dynamic_followup">
                      <div class="field-label">URL <small class="text-muted">(automático)</small></div>
                      <div class="input-wrap">
                        <input class="tpl-input" value="Link individual de cada participante (Follow Up)" disabled>
                      </div>
                      <small class="text-muted d-block">Preenchido automaticamente no envio por mudança de status. Não editável.</small>
                    </template>
                    <template v-else>
                      <div class="field-label">URL <small class="text-muted">(máx. 2000 caracteres)</small></div>
                      <div class="input-wrap">
                        <input v-model="btn.url" class="tpl-input" maxlength="2000" placeholder="https://exemplo.com">
                      </div>
                    </template>
                  </div>
                </div>

                <div v-if="form.buttons.length < 3" class="btn-add-wrap">
                  <button class="btn-add" @click="showBtnDropdown = !showBtnDropdown">
                    <i class="mdi mdi-plus"></i> Adicionar botão
                  </button>
                  <div class="btn-dropdown" :class="{ show: showBtnDropdown }">
                    <button class="dd-item" @click="addButton('QUICK_REPLY')">Resposta rápida<span class="dd-sub">Botão de resposta personalizado (máx. 25 caracteres)</span></button>
                    <button class="dd-item" @click="addButton('URL')">Visitar website<span class="dd-sub">Abre uma URL externa (texto máx. 25 caracteres)</span></button>
                    <button v-if="!hasFollowUpButton" class="dd-item" @click="addButton('URL', true)">Ação de retorno<span class="dd-sub">Link individual do Follow Up, inserido automaticamente</span></button>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <!-- Preview sidebar (fixed) -->
          <div class="tpl-panel-preview">
            <TemplatePreview
              :headerType="form.headerType"
              :headerText="form.headerText"
              :headerImageUrl="headerPreviewUrl"
              :headerVideoUrl="headerVideoPreviewUrl"
              :headerDocumentName="headerDocumentFileName"
              :bodyText="form.bodyText"
              :footerText="form.footerText"
              :buttons="form.buttons"
              :examples="form.examples"
            />
          </div>
        </div>
      </template>

    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import TemplatePreview from './components/TemplatePreview'
import appConfig from '@/app.config'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { createTemplate, updateTemplate } from './services'
import http from '@/http'

export default {
  components: { Layout, TemplatePreview },
  name: 'WATemplateCreate',
  page: { title: 'Criar Template', meta: [{ name: 'description', content: appConfig.description }] },
  data () {
    return {
      step: 1,
      submitted: false,
      saving: false,
      nameError: '',
      nameCheckTimer: null,
      showBtnDropdown: false,
      showVarDropdown: false,
      headerFile: null,
      headerFileName: '',
      headerPreviewUrl: '',
      headerVideoFile: null,
      headerVideoFileName: '',
      headerVideoPreviewUrl: '',
      headerDocumentFile: null,
      headerDocumentFileName: '',
      headerDocumentPreviewUrl: '',
      isEditing: false,
      editId: null,
      variableMap: {},
      form: { name: '', category: 'UTILITY', language_code: 'pt_BR', headerType: 'NONE', headerText: '', bodyText: '', footerText: '', buttons: [], examples: {} }
    }
  },
  computed: {
    isMediaHeader () { return ['IMAGE', 'VIDEO', 'DOCUMENT', 'LOCATION'].includes(this.form.headerType) },
    hasFollowUpButton () { return this.form.buttons.some(b => b.dynamic_followup) },
    fullTemplateName () {
      return 'prefixo_' + (this.form.name || 'nome')
    },
    variableCount () {
      if (!this.form.bodyText) return 0
      const m = this.form.bodyText.match(/\{\{(\d+)\}\}/g)
      return m ? Math.max(...m.map(x => parseInt(x.replace(/[{}]/g, '')))) : 0
    },
    variableOptions () {
      return [
        { key: 'nome', label: 'Nome do participante', tag: 'nome' },
        { key: 'email', label: 'E-mail', tag: 'email' },
        { key: 'evento', label: 'Nome do evento', tag: 'evento' },
        { key: 'data', label: 'Data do evento', tag: 'data' },
        { key: 'local', label: 'Local do evento', tag: 'local' },
        { key: 'custom', label: 'Variável personalizada', tag: 'custom' }
      ]
    }
  },
  mounted () {
    document.body.classList.add('tpl-builder-page')
    if (this.$route.params.id) { this.isEditing = true; this.editId = this.$route.params.id; this.step = 2; this.loadTemplate() }
    document.addEventListener('click', this.closeDropdowns)
  },
  beforeDestroy () {
    document.body.classList.remove('tpl-builder-page')
    document.removeEventListener('click', this.closeDropdowns)
  },
  methods: {
    closeDropdowns (e) {
      if (!e.target.closest('.btn-add-wrap')) this.showBtnDropdown = false
      if (!e.target.closest('.var-wrap')) this.showVarDropdown = false
    },
    varLabel (n) { return '{{' + n + '}}' },
    varFriendlyName (n) { const map = { nome: 'Nome', email: 'E-mail', evento: 'Evento', data: 'Data', local: 'Local', custom: 'Custom' }; return map[this.variableMap[n]] || '' },
    varPlaceholder (n) { const h = { nome: 'Ex: João', email: 'Ex: joao@email.com', evento: 'Ex: Casamento Silva', data: 'Ex: 15/06/2026', local: 'Ex: São Paulo' }; return h[this.variableMap[n]] || 'Ex: valor' },
    onNameInput () {
      // Sanitizar: só lowercase, números e underscores
      this.form.name = this.form.name.toLowerCase().replace(/[^a-z0-9_]/g, '')
      this.validateName()
    },
    validateName () {
      const name = this.form.name
      this.nameError = ''
      if (!name) return
      if (/^[0-9_]/.test(name)) {
        this.nameError = 'Nome deve começar com uma letra.'
        return
      }
      if (name.length < 3) {
        this.nameError = 'Nome deve ter no mínimo 3 caracteres.'
        return
      }
      if (/__/.test(name)) {
        this.nameError = 'Nome não pode ter underscores consecutivos.'
        return
      }
      if (/_$/.test(name)) {
        this.nameError = 'Nome não pode terminar com underscore.'
        return
      }
      // Checar duplicado no backend (debounce)
      if (this.nameCheckTimer) clearTimeout(this.nameCheckTimer)
      this.nameCheckTimer = setTimeout(() => {
        this.checkNameExists(name)
      }, 500)
    },
    async checkNameExists (name) {
      try {
        const res = await http.get('/admin/wa/templates', { params: { search: name, limit: 100 } })
        const templates = res.data?.data || []
        const exists = templates.some(t => {
          const tplName = t.name || ''
          // Comparar a parte sem prefixo
          const parts = tplName.split('_')
          const nameWithoutPrefix = parts.length > 1 ? parts.slice(1).join('_') : tplName
          return nameWithoutPrefix === name
        })
        if (exists && !this.isEditing) {
          this.nameError = 'Já existe um template com esse nome.'
        }
      } catch (e) {
        // Silently ignore check errors
      }
    },
    goToStep2 () {
      this.submitted = true
      if (!this.form.name || !this.form.category) {
        errorMessage('Preencha nome e categoria.')
        return
      }
      if (this.nameError) {
        errorMessage(this.nameError)
        return
      }
      this.submitted = false
      this.step = 2
    },
    insertNamedVariable (v) {
      const n = this.variableCount + 1; this.variableMap[n] = v.key
      this.form.bodyText = (this.form.bodyText || '') + '{{' + n + '}}'
      const d = { nome: 'João', email: 'joao@email.com', evento: 'Casamento Silva', data: '15/06/2026', local: 'São Paulo' }
      if (d[v.key]) this.$set(this.form.examples, n, d[v.key])
      this.showVarDropdown = false
    },
    wrapSelection (c) { const t = this.$el.querySelector('.tpl-textarea'); if (!t) return; const s = t.selectionStart; const e = t.selectionEnd; const x = this.form.bodyText || ''; if (s === e) return; this.form.bodyText = x.slice(0, s) + c + x.slice(s, e) + c + x.slice(e) },
    /**
     * Limpa estado quando o operador muda o tipo de header pra evitar
     * inconsistências (ex.: trocar de IMAGE pra QRCODE_PARTICIPANT mas
     * deixar o file ainda anexado).
     */
    setHeaderType (type) {
      this.form.headerType = type
      if (type !== 'IMAGE') { this.headerFile = null; this.headerFileName = ''; this.headerPreviewUrl = null }
      if (type !== 'VIDEO') { this.headerVideoFile = null; this.headerVideoFileName = ''; this.headerVideoPreviewUrl = null }
      if (type !== 'DOCUMENT') { this.headerDocumentFile = null; this.headerDocumentFileName = ''; this.headerDocumentPreviewUrl = null }
      if (type !== 'TEXT') { this.form.headerText = '' }
    },
    onHeaderFileChange (e) { const f = e.target.files[0]; if (!f) return; if (f.size > 5242880) { errorMessage('Máx 5MB.'); return }; this.headerFile = f; this.headerFileName = f.name; this.headerPreviewUrl = URL.createObjectURL(f) },
    onHeaderVideoChange (e) { const f = e.target.files[0]; if (!f) return; if (f.size > 16777216) { errorMessage('Máx 16MB.'); return }; this.headerVideoFile = f; this.headerVideoFileName = f.name; this.headerVideoPreviewUrl = URL.createObjectURL(f) },
    onHeaderDocumentChange (e) { const f = e.target.files[0]; if (!f) return; if (f.size > 104857600) { errorMessage('Máx 100MB.'); return }; this.headerDocumentFile = f; this.headerDocumentFileName = f.name; this.headerDocumentPreviewUrl = URL.createObjectURL(f) },
    addButton (t, dynamicFollowup = false) { this.form.buttons.push({ type: t, text: dynamicFollowup ? 'COMPLETAR CADASTRO' : '', url: '', dynamic_followup: dynamicFollowup }); this.showBtnDropdown = false },
    removeButton (i) { this.form.buttons.splice(i, 1) },
    buildVariablesMap () {
      // Returns the variableMap as-is (frontend keys like 'nome', 'evento', 'custom')
      // The backend resolver converts them to internal keys (participant.name, event.name, etc.)
      const map = {}
      Object.keys(this.variableMap).forEach(pos => {
        map[String(pos)] = this.variableMap[pos]
      })
      return map
    },
    buildComponents () {
      const c = []
      if (this.form.headerType === 'TEXT' && this.form.headerText) c.push({ type: 'HEADER', format: 'TEXT', text: this.form.headerText })
      else if (this.isMediaHeader) c.push({ type: 'HEADER', format: this.form.headerType })
      // QRCODE_PARTICIPANT é um header IMAGE com sample bundled — backend
      // injeta a imagem fixa do QR genérico ao chamar a Meta. No envio real,
      // é substituído pelo QR único do participante.
      else if (this.form.headerType === 'QRCODE_PARTICIPANT') c.push({ type: 'HEADER', format: 'IMAGE' })
      if (this.form.bodyText) { const b = { type: 'BODY', text: this.form.bodyText }; if (this.variableCount > 0) { const v = []; for (let i = 1; i <= this.variableCount; i++) v.push(this.form.examples[i] || 'ex' + i); b.example = { body_text: [v] } }; c.push(b) }
      if (this.form.footerText) c.push({ type: 'FOOTER', text: this.form.footerText })
      if (this.form.buttons.length) { const bs = this.form.buttons.filter(b => b.text).map(b => b.type === 'URL' ? (b.dynamic_followup ? { type: 'URL', text: b.text, dynamic_followup: true } : { type: 'URL', text: b.text, url: b.url }) : { type: 'QUICK_REPLY', text: b.text }); if (bs.length) c.push({ type: 'BUTTONS', buttons: bs }) }
      return c
    },
    async handleSubmit () {
      this.submitted = true
      if (!this.form.name || !this.form.bodyText) {
        errorMessage('Nome e corpo são obrigatórios.')
        return
      }
      if (this.nameError) {
        errorMessage(this.nameError)
        return
      }
      // Validar variáveis sequenciais
      if (this.variableCount > 0) {
        const body = this.form.bodyText
        for (let i = 1; i <= this.variableCount; i++) {
          if (!body.includes('{{' + i + '}}')) {
            errorMessage('Variáveis devem ser sequenciais. Falta {{' + i + '}} no corpo.')
            return
          }
        }
      }
      // Validar exemplos
      for (let i = 1; i <= this.variableCount; i++) {
        if (!this.form.examples[i]) {
          errorMessage('Preencha o exemplo da variável ' + this.varLabel(i) + '.')
          return
        }
      }
      // Validar botões
      for (let i = 0; i < this.form.buttons.length; i++) {
        const btn = this.form.buttons[i]
        if (!btn.text || !btn.text.trim()) {
          errorMessage('Preencha o texto do botão ' + (i + 1) + '.')
          return
        }
        if (btn.type === 'URL' && !btn.dynamic_followup && (!btn.url || !btn.url.trim())) {
          errorMessage('Preencha a URL do botão "' + btn.text + '".')
          return
        }
        if (btn.type === 'URL' && !btn.dynamic_followup && btn.url && !btn.url.startsWith('https://')) {
          errorMessage('A URL do botão "' + btn.text + '" deve começar com https://')
          return
        }
      }
      this.saving = true
      try {
        const comp = this.buildComponents()
        const variables = this.buildVariablesMap()
        if (this.isEditing) {
          await updateTemplate(this.editId, { components: comp, variables })
          successMessage('Template atualizado.', 3000)
        } else {
          const hasMedia = this.headerFile || this.headerVideoFile || this.headerDocumentFile
          const isDynamicQr = this.form.headerType === 'QRCODE_PARTICIPANT'
          // Mapeia tipo do frontend pro valor que o backend grava
          const dynamicHeaderType = isDynamicQr ? 'participant_qrcode' : null
          if (hasMedia || isDynamicQr) {
            const fd = new FormData()
            fd.append('name', this.form.name)
            fd.append('category', this.form.category)
            fd.append('language_code', this.form.language_code)
            fd.append('components', JSON.stringify(comp))
            fd.append('variables', JSON.stringify(variables))
            if (this.headerFile) fd.append('header_image', this.headerFile)
            if (this.headerVideoFile) fd.append('header_video', this.headerVideoFile)
            if (this.headerDocumentFile) fd.append('header_document', this.headerDocumentFile)
            if (dynamicHeaderType) fd.append('dynamic_header_type', dynamicHeaderType)
            await createTemplate(fd)
          } else {
            await createTemplate({
              name: this.form.name,
              category: this.form.category,
              language_code: this.form.language_code,
              components: comp,
              variables,
              dynamic_header_type: dynamicHeaderType
            })
          }
          successMessage('Template criado e enviado para aprovação.', 3000)
        }
        this.$router.push('/wa/template')
      } catch (err) {
        errorMessage('Falha: ' + (err.response?.data?.message || err.message))
      } finally {
        this.saving = false
      }
    },
    async loadTemplate () {
      const resolverToFrontend = {
        'participant.name': 'nome',
        'participant.email': 'email',
        'event.name': 'evento',
        'event.date': 'data',
        'event.location': 'local',
        'custom': 'custom'
      }
      try {
        let l = this.$loading.show(); const r = await http.get('/admin/wa/templates'); const t = (r.data?.data || []).find(x => x.id === parseInt(this.editId))
        if (t) {
          this.form.name = t.name; this.form.category = t.category || 'UTILITY'
          // Restore variable map from saved variables
          if (t.variables && Object.keys(t.variables).length) {
            const map = {}
            Object.keys(t.variables).forEach(pos => {
              map[parseInt(pos)] = resolverToFrontend[t.variables[pos]] || t.variables[pos]
            })
            this.variableMap = map
          }
          ;(t.components || []).forEach(c => { if (c.type === 'HEADER') { this.form.headerType = c.format || 'TEXT'; if (c.format === 'TEXT') this.form.headerText = c.text || '' }; if (c.type === 'BODY') { this.form.bodyText = c.text || ''; if (c.example?.body_text?.[0]) c.example.body_text[0].forEach((v, i) => this.$set(this.form.examples, i + 1, v)) }; if (c.type === 'FOOTER') this.form.footerText = c.text || ''; if (c.type === 'BUTTONS') this.form.buttons = (c.buttons || []).map(b => ({ type: b.type, text: b.text || '', url: b.url || '', dynamic_followup: b.type === 'URL' && /acao-retorno=\{\{1\}\}/.test(b.url || '') })) })
        }
        l.hide()
      } catch (e) { errorMessage('Falha ao carregar.') }
    }
  }
}
</script>

<style scoped>
/* ══════════════════════════════════════════
   FULLSCREEN BUILDER LAYOUT
   ══════════════════════════════════════════ */
.tpl-builder {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f6fa;
  overflow: hidden;
}

/* Header */
.tpl-builder-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}

.header-left { display: flex; align-items: center; gap: 14px; }
.header-right { display: flex; align-items: center; gap: 8px; }

.btn-back {
  width: 36px; height: 36px; display: flex; align-items: center; justify-content: center;
  border: 1px solid #e9ecef; background: #fff; border-radius: 8px; cursor: pointer; color: #495057; transition: all .15s;
}
.btn-back:hover { border-color: #556ee6; color: #556ee6; }

.header-title { font-size: 16px; font-weight: 600; color: #344054; }
.header-subtitle { font-size: 13px; color: #6c757d; padding-left: 14px; border-left: 1px solid #e9ecef; }

.btn-action-primary {
  display: flex; align-items: center; gap: 6px; padding: 8px 20px; border: none; background: #556ee6; color: #fff;
  border-radius: 6px; font-family: inherit; font-size: 13px; font-weight: 500; cursor: pointer; transition: all .15s;
}
.btn-action-primary:hover:not(:disabled) { background: #485ec4; }
.btn-action-primary:disabled { opacity: .6; cursor: not-allowed; }

.btn-action-secondary {
  padding: 8px 16px; border: 1px solid #e9ecef; background: #fff; border-radius: 6px;
  font-family: inherit; font-size: 13px; color: #495057; cursor: pointer; transition: all .15s;
}
.btn-action-secondary:hover { border-color: #556ee6; color: #556ee6; }

/* Step 1 */
.step1-content {
  flex: 1; overflow-y: auto; padding: 24px 28px; background: #fff;
}
.step1-inner { width: 100%; }

/* Step 2 split */
.tpl-builder-content {
  display: flex; flex: 1; min-height: 0; overflow: hidden;
}

.tpl-panel-main {
  flex: 1; display: flex; flex-direction: column; min-width: 0; background: #fff;
}

.tpl-panel-scroll {
  flex: 1; overflow-y: auto; min-height: 0;
  scrollbar-width: thin; scrollbar-color: #d0d5dd transparent;
}
.tpl-panel-scroll::-webkit-scrollbar { width: 6px; }
.tpl-panel-scroll::-webkit-scrollbar-track { background: transparent; }
.tpl-panel-scroll::-webkit-scrollbar-thumb { background: #d0d5dd; border-radius: 3px; }
.tpl-panel-scroll::-webkit-scrollbar-thumb:hover { background: #b0b5bd; }

.tpl-panel-preview {
  width: 340px; flex-shrink: 0; background: #fff; border-left: 1px solid #e9ecef; overflow-y: auto;
}

/* Sections */
.section {
  padding: 24px 28px; border-bottom: 1px solid #f0f2f5;
}

.section-head { margin-bottom: 12px; }

/* Field groups */
.field-group { margin-bottom: 24px; }
.field-group:last-child { margin-bottom: 0; }

.field-title { font-size: 14px; font-weight: 600; color: #344054; margin-bottom: 4px; }
.field-desc { font-size: 12px; color: #667085; margin-bottom: 10px; line-height: 1.5; }
.field-label { font-size: 12px; color: #667085; font-weight: 500; margin-bottom: 4px; }
.field-error { font-size: 12px; color: #EF4444; margin-top: 4px; }
.field-hint { font-size: 12px; color: #667085; margin-top: 4px; display: flex; align-items: center; gap: 4px; }
.field-hint strong { color: #344054; font-family: monospace; font-size: 11px; background: #f0f2f5; padding: 1px 6px; border-radius: 3px; }

.tag-opt {
  font-size: 10px; font-weight: 600; text-transform: uppercase; color: #667085;
  background: #f0f2f5; padding: 2px 7px; border-radius: 4px; border: 1px solid #e9ecef; vertical-align: middle;
}

/* Inputs */
.tpl-input {
  width: 100%; padding: 0.47rem 0.75rem; border: 1px solid #ced4da; border-radius: 0.25rem;
  font-family: inherit; font-size: 0.8125rem; color: #495057; background: #fff; outline: none; transition: border-color .15s, box-shadow .15s;
}
.tpl-input:focus { border-color: #556ee6; box-shadow: 0 0 0 3px rgba(85,110,230,.08); }
.tpl-input::placeholder { color: #adb5bd; }
.tpl-input.error { border-color: #EF4444; }
.tpl-input-sm { padding: 0.35rem 0.65rem; font-size: 0.8125rem; }
.tpl-textarea { resize: vertical; min-height: 120px; line-height: 1.6; }

.input-wrap { position: relative; }
.counter { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); font-size: 11px; color: #98a2b3; pointer-events: none; }
.counter-textarea { top: auto !important; bottom: 10px !important; transform: none !important; }

/* Category */
.cat-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.cat-card { display: flex; gap: 14px; padding: 18px; border: 1.5px solid #e9ecef; border-radius: 10px; cursor: pointer; transition: all .15s; }
.cat-card:hover { border-color: rgba(85,110,230,.3); }
.cat-card.selected { border-color: #556ee6; background: rgba(85,110,230,.04); box-shadow: 0 0 0 3px rgba(85,110,230,.08); }
.cat-icon { width: 40px; height: 40px; border-radius: 10px; background: #f0f2f5; display: flex; align-items: center; justify-content: center; color: #667085; flex-shrink: 0; }
.cat-icon.active { background: rgba(85,110,230,.1); color: #556ee6; }
.cat-name { font-size: 14px; font-weight: 500; color: #344054; margin-bottom: 2px; }
.cat-desc { font-size: 12px; color: #667085; line-height: 1.4; }

/* Language */
.lang-box { display: flex; align-items: center; gap: 10px; padding: 12px 16px; background: #f9fafb; border: 1px solid #e9ecef; border-radius: 8px; font-size: 14px; color: #344054; }
.lang-lock { margin-left: auto; font-size: 11px; color: #667085; display: flex; align-items: center; gap: 4px; }

/* Pills */
.pill-group { display: flex; gap: 6px; }
.pill { padding: 7px 16px; border: 1px solid #e9ecef; border-radius: 8px; background: #fff; font-family: inherit; font-size: 13px; font-weight: 500; color: #495057; cursor: pointer; transition: all .15s; }
.pill:hover { border-color: #556ee6; color: #556ee6; }
.pill.active { background: #556ee6; border-color: #556ee6; color: #fff; }

/* Media grid */
.media-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.media-opt { padding: 16px 8px; border: 1.5px dashed #d0d5dd; border-radius: 10px; display: flex; flex-direction: column; align-items: center; gap: 6px; cursor: pointer; background: #f9fafb; transition: all .15s; }
.media-opt:hover { border-color: #556ee6; background: rgba(85,110,230,.04); }
.media-opt:hover svg { color: #556ee6; }
.media-opt.selected { border-color: #556ee6; border-style: solid; background: rgba(85,110,230,.06); }
.media-opt.selected svg { color: #556ee6; }
.media-opt svg { color: #667085; transition: color .15s; }
.media-opt span { font-size: 11px; font-weight: 600; color: #344054; }

/* Upload */
.upload-area { border: 1.5px dashed #d0d5dd; border-radius: 10px; padding: 24px; text-align: center; cursor: pointer; display: flex; flex-direction: column; align-items: center; gap: 6px; color: #667085; transition: all .15s; }
.upload-area:hover { border-color: #556ee6; background: rgba(85,110,230,.04); }
.upload-area span { font-size: 13px; font-weight: 500; color: #344054; }
.upload-area small { font-size: 11px; color: #667085; }

/* QRCode dinâmico — info box explicativo */
.qr-info-box {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  background: rgba(85, 110, 230, 0.05);
  border: 1px solid rgba(85, 110, 230, 0.2);
  border-radius: 10px;
}
.qr-info-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  background: #fff;
  border-radius: 8px;
  color: #556ee6;
}
.qr-info-content { min-width: 0; }
.qr-info-title { font-size: 13px; font-weight: 600; color: #1a1d26; margin-bottom: 4px; }
.qr-info-desc { font-size: 12px; color: #667085; line-height: 1.5; }

/* Toolbar */
.toolbar { display: flex; justify-content: space-between; align-items: center; margin-top: 8px; }
.toolbar-left { display: flex; gap: 4px; }
.toolbar-btn { padding: 5px 12px; border: 1px solid #e9ecef; border-radius: 6px; background: #fff; font-family: inherit; font-size: 12px; font-weight: 500; color: #495057; cursor: pointer; display: flex; align-items: center; gap: 4px; transition: all .1s; }
.toolbar-btn:hover { border-color: #556ee6; color: #556ee6; }

/* Variable dropdown */
.var-wrap { position: relative; }
.var-dropdown { position: absolute; top: calc(100% + 4px); left: 0; min-width: 260px; background: #fff; border: 1px solid #e9ecef; border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,.1); padding: 4px; z-index: 20; opacity: 0; pointer-events: none; transform: translateY(-4px); transition: all .15s; }
.var-dropdown.show { opacity: 1; pointer-events: all; transform: translateY(0); }
.var-dropdown-item { display: flex; align-items: center; gap: 10px; padding: 9px 12px; border-radius: 6px; cursor: pointer; font-size: 13px; color: #344054; border: none; background: none; width: 100%; text-align: left; font-family: inherit; transition: background .1s; }
.var-dropdown-item:hover { background: #f0f2f5; color: #556ee6; }
.var-tag { font-size: 10px; font-weight: 600; color: #556ee6; background: rgba(85,110,230,.1); padding: 2px 8px; border-radius: 4px; }

/* Variable examples */
.var-examples { margin-top: 16px; padding: 16px; background: #f9fafb; border: 1px solid #e9ecef; border-radius: 8px; }
.var-example-row { display: flex; align-items: center; gap: 10px; margin-top: 8px; }
.var-example-row:first-of-type { margin-top: 10px; }
.var-badge { font-size: 11px; font-weight: 700; color: #556ee6; background: rgba(85,110,230,.1); padding: 3px 10px; border-radius: 4px; white-space: nowrap; min-width: 42px; text-align: center; }
.var-friendly { font-size: 12px; color: #667085; min-width: 50px; }
.var-example-row .tpl-input { flex: 1; }

/* Button items */
.btn-item { padding: 16px; border: 1px solid #e9ecef; border-radius: 8px; background: #f9fafb; margin-bottom: 10px; }
.btn-item-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; font-size: 13px; font-weight: 600; color: #344054; }
.btn-item-delete { border: none; background: none; color: #EF4444; font-size: 12px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 4px; padding: 4px 8px; border-radius: 4px; font-family: inherit; }
.btn-item-delete:hover { background: #FEE2E2; }
.btn-item-body { margin-bottom: 10px; }
.btn-item-body:last-child { margin-bottom: 0; }

/* Button add */
.btn-add-wrap { position: relative; }
.btn-add { padding: 12px; border: 1.5px dashed #d0d5dd; border-radius: 8px; background: transparent; font-family: inherit; font-size: 13px; font-weight: 500; color: #667085; cursor: pointer; display: flex; align-items: center; gap: 6px; width: 100%; justify-content: center; transition: all .15s; }
.btn-add:hover { border-color: #556ee6; color: #556ee6; background: rgba(85,110,230,.04); }

.btn-dropdown { position: absolute; top: calc(100% + 4px); left: 0; right: 0; background: #fff; border: 1px solid #e9ecef; border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,.1); padding: 4px; z-index: 10; opacity: 0; pointer-events: none; transform: translateY(-4px); transition: all .15s; }
.btn-dropdown.show { opacity: 1; pointer-events: all; transform: translateY(0); }
.dd-item { display: block; padding: 10px 12px; border-radius: 6px; cursor: pointer; font-size: 13px; color: #344054; border: none; background: none; width: 100%; text-align: left; font-family: inherit; }
.dd-item:hover { background: #f0f2f5; color: #556ee6; }
.dd-sub { display: block; font-size: 11px; color: #667085; margin-top: 2px; }

.mt-3 { margin-top: 12px; }

/* Location info */
.location-info { display: flex; align-items: flex-start; gap: 12px; padding: 16px; background: rgba(85,110,230,.05); border: 1.5px solid rgba(85,110,230,.2); border-radius: 10px; }
.location-info-icon { width: 36px; height: 36px; border-radius: 8px; background: rgba(85,110,230,.1); display: flex; align-items: center; justify-content: center; color: #556ee6; flex-shrink: 0; }
.location-info-title { font-size: 13px; font-weight: 600; color: #344054; margin-bottom: 4px; }
.location-info-desc { font-size: 12px; color: #667085; line-height: 1.5; }
</style>

<style>
/* Global: fullscreen mode — same pattern as form-builder */
body.tpl-builder-page { overflow: hidden !important; height: 100vh !important; }
body.tpl-builder-page footer.footer { display: none !important; }
body.tpl-builder-page .main-content { padding: 0 !important; margin-top: 0 !important; margin-bottom: 0 !important; margin-right: 0 !important; overflow: hidden !important; height: 100vh !important; display: flex !important; flex-direction: column !important; }
body.tpl-builder-page .page-content { padding: 0 !important; margin: 0 !important; margin-top: 70px !important; overflow: hidden !important; min-height: 0 !important; height: calc(100vh - 70px) !important; max-height: calc(100vh - 70px) !important; flex: 1 !important; display: flex !important; flex-direction: column !important; }
body.tpl-builder-page .page-content > .container-fluid { padding: 0 !important; margin: 0 !important; height: 100% !important; min-height: 0 !important; max-width: 100% !important; flex: 1 !important; display: flex !important; flex-direction: column !important; }
</style>
